/* ─────────────────────────────────────────────────────────────────
   JAVA FULL STACK DEVELOPMENT — MODULE-BASED COURSE CONTENT
   Imported by MicrointernshipTask.jsx
───────────────────────────────────────────────────────────────── */

export const JAVA_FULLSTACK_MODULES = [
  {
    module: 1,
    title: "Core Java Fundamentals",
    emoji: "☕",
    topics: [
      {
        id: 1,
        topic: "Java Syntax & OOP Basics",
        description: "Learn Java's syntax, classes, objects, and the four pillars of object-oriented programming.",
        motivation: "Great start! Java powers a huge share of enterprise software — you just wrote your foundation for it. ☕",
        resource: {
          name: "Oracle Java Docs — Classes and Objects",
          url: "https://docs.oracle.com/javase/tutorial/java/javaOO/index.html",
        },
        task: "Build a Library Management System (console-based) using OOP principles.",
        requirements: [
          "Create a Book class with title, author, ISBN, and availability status",
          "Create a Library class that can add books and issue/return them",
          "Use encapsulation — private fields with getters/setters",
          "Demonstrate inheritance with an EBook subclass (adds a file size field)",
          "Print a formatted list of all books and their status",
          "Use a main() method to demonstrate at least 5 operations",
        ],
      },
      {
        id: 2,
        topic: "Collections & Exception Handling",
        description: "Learn Java's Collections Framework (ArrayList, HashMap) and how to handle errors using try/catch.",
        motivation: "Collections and exceptions conquered! You can now build programs that manage real data safely. 🧰",
        resource: {
          name: "Oracle Java Docs — Collections Framework Overview",
          url: "https://docs.oracle.com/javase/tutorial/collections/intro/index.html",
        },
        task: "Build a Student Record Management System using Collections.",
        requirements: [
          "Store students in an ArrayList of a Student class",
          "Use a HashMap to map roll numbers to Student objects for fast lookup",
          "Add, search, update, and delete student records",
          "Calculate and display class average and topper",
          "Handle invalid input (e.g. duplicate roll number) using custom exceptions",
          "Wrap risky operations in try/catch/finally blocks",
        ],
      },
    ],
  },
  {
    module: 2,
    title: "Spring Boot Backend",
    emoji: "🌱",
    topics: [
      {
        id: 3,
        topic: "Spring Boot REST API Basics",
        description: "Learn to build REST APIs using Spring Boot — the standard framework for Java backend development.",
        motivation: "You built your first Spring Boot API! This is the exact framework used by countless enterprise backends. 🚀",
        resource: {
          name: "Spring Boot Official Guide — Building a RESTful Web Service",
          url: "https://spring.io/guides/gs/rest-service",
        },
        task: "Build a REST API for a Task Manager using Spring Boot.",
        requirements: [
          "Endpoints: GET /tasks, POST /tasks, PUT /tasks/{id}, DELETE /tasks/{id}",
          "Use a Task model with id, title, description, and completed fields",
          "Store tasks in an in-memory list or map (no database needed yet)",
          "Return proper HTTP status codes (200, 201, 404, 400)",
          "Test all endpoints using Postman or Thunder Client",
          "Include a README.md with setup and run instructions",
        ],
      },
      {
        id: 4,
        topic: "Spring Data JPA & Database Integration",
        description: "Learn to connect Spring Boot to a real database using Spring Data JPA.",
        motivation: "Your data persists now! Connecting Spring Boot to a real database is a core enterprise-dev skill. 🗄️",
        resource: {
          name: "Spring Boot Official Guide — Accessing Data with JPA",
          url: "https://spring.io/guides/gs/accessing-data-jpa",
        },
        task: "Add MySQL or H2 database persistence to your Task Manager API.",
        requirements: [
          "Define a Task entity with JPA annotations (@Entity, @Id, etc.)",
          "Create a TaskRepository extending JpaRepository",
          "All CRUD operations must persist to the database",
          "Add a completed-tasks filter endpoint (GET /tasks?completed=true)",
          "Configure application.properties for your database connection",
          "Handle 'task not found' errors with a proper 404 response",
        ],
      },
    ],
  },
  {
    module: 3,
    title: "Frontend Integration & Security",
    emoji: "🔗",
    topics: [
      {
        id: 5,
        topic: "Connecting a Frontend to Your Spring Boot API",
        description: "Learn to connect a frontend (React or Thymeleaf) to your Spring Boot backend and handle CORS.",
        motivation: "Full stack connection made! Your Java backend now talks to a real frontend — that's the full picture. 🌐",
        resource: {
          name: "Spring Boot Official Guide — Enabling Cross-Origin Requests (CORS)",
          url: "https://spring.io/guides/gs/rest-service-cors",
        },
        task: "Build a simple frontend (React or Thymeleaf) that consumes your Task Manager API.",
        requirements: [
          "Display all tasks fetched from the Spring Boot API",
          "Form to add a new task from the UI",
          "Button to mark a task as completed",
          "Button to delete a task",
          "Configure CORS on the Spring Boot side so the frontend can call it",
          "Show loading and error states while fetching",
        ],
      },
      {
        id: 6,
        topic: "JWT Authentication in Spring Boot",
        description: "Learn to secure your Spring Boot API using Spring Security and JWT tokens.",
        motivation: "Security unlocked! You now know how enterprise Java applications protect their APIs. 🔐",
        resource: {
          name: "Spring Boot Official Guide — Securing a Web Application",
          url: "https://spring.io/guides/gs/securing-web",
        },
        task: "Add user registration, login, and JWT-protected routes to your Task Manager API.",
        requirements: [
          "POST /auth/register — hash passwords with BCrypt",
          "POST /auth/login — return a JWT token on success",
          "Protect POST/PUT/DELETE task routes with JWT authentication",
          "Return 401 Unauthorized for missing or invalid tokens",
          "Associate tasks with the logged-in user",
          "Test protected routes with and without a valid token",
        ],
      },
    ],
  },
  {
    module: 4,
    title: "Final Project",
    emoji: "🏆",
    topics: [
      {
        id: 7,
        topic: "Full Stack Employee Management System",
        description: "Apply everything learned throughout the MicroIntern to build a complete Java full stack application.",
        motivation: "Outstanding! You built a secure, full stack Java application end-to-end. That's certificate-worthy. 🎉",
        resource: {
          name: "Spring Boot Official Guides",
          url: "https://spring.io/guides",
        },
        task: "Build a full stack Employee Management System with Spring Boot backend and a React or Thymeleaf frontend.",
        requirements: [
          "User login with JWT authentication",
          "CRUD operations for employees (name, department, salary, role)",
          "Data persisted in MySQL or H2 via Spring Data JPA",
          "Frontend to list, add, edit, and delete employees",
          "Search/filter employees by department",
          "Proper error handling on both frontend and backend",
          "Clean project structure with a well-documented README.md",
        ],
      },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────────
   Flat array — used by COURSE_TASKS in MicrointernshipTask.jsx
   Preserves backward-compatible indexing for completedTopics[]
───────────────────────────────────────────────────────────────── */
export const JAVA_FULLSTACK_TASKS = JAVA_FULLSTACK_MODULES.flatMap((m) => m.topics);
